import { keyframes } from '@mui/system';

/**
 * Animation Library - Premium motion design for EAS Calculator
 *
 * Provides consistent, refined animations with:
 * - Confident, purposeful motion
 * - Springy and refined easing functions
 * - Blue-tinted glows matching dashboard theme
 */

// ============================================================================
// ENTRANCE ANIMATIONS
// ============================================================================

/**
 * Smooth slide-in from below
 * Use for: Card reveals, section entrances
 * Duration: 0.4-0.6s
 */
export const smoothSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Fade with scale
 * Use for: Small elements, icons, badges
 * Duration: 0.3-0.5s
 */
export const fadeScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// ============================================================================
// ATTENTION ANIMATIONS
// ============================================================================

/**
 * Refined blue glow pulse
 * Use for: Active elements, upload zones, focus states
 * Duration: 1.5-2s infinite
 */
export const refinedGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(25, 118, 210, 0);
  }
  50% {
    box-shadow: 0 0 40px rgba(25, 118, 210, 0.25);
  }
`;

/**
 * Refined glow for dark mode
 * Use for: Dark mode hover states, glows
 * Duration: 1.5-2s infinite
 */
export const refinedGlowDark = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(100, 181, 246, 0);
  }
  50% {
    box-shadow: 0 0 40px rgba(100, 181, 246, 0.3);
  }
`;

// ============================================================================
// SUCCESS ANIMATIONS
// ============================================================================

/**
 * Checkmark burst animation
 * Use for: File upload success, form submission
 * Duration: 0.6s
 */
export const checkmarkBurst = keyframes`
  0% {
    transform: scale(0) rotate(-180deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(10deg);
    opacity: 1;
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
`;

/**
 * Success ripple
 * Use for: Success confirmations, completions
 * Duration: 0.8s
 */
export const successRipple = keyframes`
  0% {
    transform: scale(0);
    opacity: 0.8;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
`;

// ============================================================================
// BACKGROUND FLOWS
// ============================================================================

/**
 * Subtle background gradient flow
 * Use for: Hero sections, large containers
 * Duration: 10-12s infinite
 */
export const subtleFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/**
 * Diagonal pattern flow
 * Use for: Upload zones, active drag states
 * Duration: 20s infinite
 */
export const diagonalFlow = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 40px 40px; }
`;

// ============================================================================
// VALUE UPDATES
// ============================================================================

/**
 * Value shimmer on data update
 * Use for: Metric values, statistics
 * Duration: 0.3s
 */
export const valueShimmer = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(1.03);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

/**
 * Number counter animation
 * Use for: Counting up to final value
 * Duration: 1s
 */
export const numberCount = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ============================================================================
// PROGRESS INDICATORS
// ============================================================================

/**
 * Progress bar fill
 * Use for: Loading states, completion bars
 * Duration: Variable based on use case
 */
export const progressFill = keyframes`
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
`;

/**
 * Spinner rotation
 * Use for: Loading indicators
 * Duration: 1s infinite
 */
export const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// ============================================================================
// EASING FUNCTIONS
// ============================================================================

/**
 * Springy easing - overshoots slightly for bounce effect
 * Use for: Hover lifts, playful interactions
 * Example: transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)
 */
export const springEasing = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/**
 * Refined easing - smooth and sophisticated
 * Use for: Card reveals, page transitions
 * Example: transition: all 0.4s cubic-bezier(0.22, 0.61, 0.36, 1)
 */
export const refinedEasing = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

/**
 * Snap easing - fast start, slow end
 * Use for: Closing dialogs, dismissing elements
 * Example: transition: opacity 0.2s cubic-bezier(0.4, 0, 1, 1)
 */
export const snapEasing = 'cubic-bezier(0.4, 0, 1, 1)';

// ============================================================================
// USAGE EXAMPLES
// ============================================================================

/**
 * Example 1: Staggered card reveal in ExecutiveDashboard
 *
 * <Grid item sx={{
 *   animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
 *   animationDelay: `${index * 0.1}s`,
 *   animationFillMode: 'backwards'
 * }}>
 *   <MetricCard />
 * </Grid>
 */

/**
 * Example 2: Value shimmer on data update in MetricCard
 *
 * const [shimmer, setShimmer] = useState(false);
 *
 * useEffect(() => {
 *   setShimmer(true);
 *   const timer = setTimeout(() => setShimmer(false), 300);
 *   return () => clearTimeout(timer);
 * }, [value]);
 *
 * <Typography sx={{
 *   animation: shimmer ? `${valueShimmer} 0.3s ease` : 'none'
 * }}>
 *   {value}
 * </Typography>
 */

/**
 * Example 3: Upload zone with glow and diagonal pattern
 *
 * <Box sx={{
 *   animation: isDragActive ? `${refinedGlow} 1.5s infinite` : 'none',
 *   background: isDragActive
 *     ? `repeating-linear-gradient(
 *         45deg,
 *         rgba(25, 118, 210, 0.03),
 *         rgba(25, 118, 210, 0.03) 10px,
 *         transparent 10px,
 *         transparent 20px
 *       )`
 *     : 'transparent',
 *   backgroundSize: '40px 40px',
 *   backgroundAnimation: isDragActive ? `${diagonalFlow} 20s linear infinite` : 'none'
 * }}>
 *   Drop zone content
 * </Box>
 */

/**
 * Example 4: Success checkmark burst
 *
 * {fileUploaded && (
 *   <CheckCircleIcon sx={{
 *     animation: `${checkmarkBurst} 0.6s ${springEasing}`,
 *     color: 'primary.main',
 *     fontSize: 48
 *   }} />
 * )}
 */
